import { useCallback, useEffect, useState } from "react";

export type Theme = "light" | "dark" | "system";

interface UseThemeProps
{
  defaultTheme?: Theme;
  storageKey?: string;
}

interface UseThemeReturn
{
  theme: Theme;
  setTheme: (theme: Theme) => void;
  resolvedTheme: "light" | "dark";
}

export const useTheme = ({ defaultTheme = "system", storageKey = "theme" }: UseThemeProps = {}): UseThemeReturn =>
{
  const [theme, setThemeState] = useState<Theme>(defaultTheme);
  const [mounted, setMounted] = useState(false);

  // Get system theme preference
  const getSystemTheme = useCallback((): "light" | "dark" =>
  {
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  }, []);

  // Get resolved theme (actual theme that should be applied)
  const getResolvedTheme = useCallback(
    (currentTheme: Theme): "light" | "dark" =>
    {
      return currentTheme === "system" ? getSystemTheme() : currentTheme;
    },
    [getSystemTheme]
  );

  // Apply theme to DOM
  const applyTheme = useCallback((resolvedTheme: "light" | "dark") =>
  {
    const root = document.documentElement;
    if (resolvedTheme === "dark")
    {
      root.classList.add("dark");
    }
    else
    {
      root.classList.remove("dark");
    }
  }, []);

  // Load theme from localStorage on mount
  useEffect(() =>
  {
    const storedTheme = localStorage.getItem(storageKey) as Theme;
    if (storedTheme && ["light", "dark", "system"].includes(storedTheme))
    {
      setThemeState(storedTheme);
    }
    setMounted(true);
  }, [storageKey]);

  // Apply theme when it changes
  useEffect(() =>
  {
    if (!mounted) return;

    const resolvedTheme = getResolvedTheme(theme);
    applyTheme(resolvedTheme);
  }, [theme, mounted, getResolvedTheme, applyTheme]);

  // Listen for system theme changes when using system theme
  useEffect(() =>
  {
    if (!mounted || theme !== "system") return;

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = () =>
    {
      const resolvedTheme = getResolvedTheme(theme);
      applyTheme(resolvedTheme);
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, [theme, mounted, getResolvedTheme, applyTheme]);

  // Set theme function with localStorage persistence
  const setTheme = useCallback(
    (newTheme: Theme) =>
    {
      setThemeState(newTheme);
      localStorage.setItem(storageKey, newTheme);
    },
    [storageKey]
  );

  const resolvedTheme = getResolvedTheme(theme);

  return {
    theme,
    setTheme,
    resolvedTheme,
  };
};
